import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createRowStatusSelector} from 'notebook/tables/store/selectors/rowModel/RowStatusSelectors';
import {
  createDebuggableSelector,
  createDeepSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import {ColumnConstraint, Row, RowData, RowProperties} from 'notebook/tables/TableConstants';

/**
 * This holds volatile values of the model, or values that frequently changes due to evaluation or renders.
 * This should contain volatile values from the TableModel that are only relevant to this single row.
 *
 * Everything inside here will be deep-compare memoized.
 */
export interface VolatileRowModel {
  row: Row;
  evaluatedRow: Array<EvaluatedCell>;
  rowData: RowData | null;
  columnConstraints: Array<ColumnConstraint | null>;
}

/**
 * This model is what is passed down to the components, as well as to the CellModel.
 * This can contain data that is derived from the TableModel or TableEditableStore
 */
export interface RowModel extends VolatileRowModel {
  rowProperties: RowProperties;
}

function createVolatileRowModelSelector(
  rowIndex: number,
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => VolatileRowModel {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [
      // row
      (store) => tableSelector(store).props.table.table[rowIndex],
      // evaluatedRow
      (store) => tableSelector(store).props.computedTable[rowIndex],
      // rowData
      (store) => tableSelector(store).props.table.rowData?.[rowIndex] ?? null,
      // columnConstraints
      (store) => tableSelector(store).props.table.columnConstraints,
    ],
    (row, evaluatedRow, rowData, columnConstraints) => ({
      row,
      evaluatedRow,
      rowData,
      columnConstraints,
    })
  );
}

export function createRowModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  rowIndex: number
): (store: TableEditableStore) => RowModel {
  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      // volatileStore
      createVolatileRowModelSelector(rowIndex, tableSelector),
      // rowProperties
      createRowStatusSelector(tableSelector, rowIndex),
    ],
    (volatileModel, rowStatus): RowModel => ({
      ...volatileModel,
      // For now, leaving this named as rowProperties since this naming propagates through a lot of code
      // But this should be renamed because rowProperties is not specific
      rowProperties: rowStatus,
    })
  );
}
